import * as React from 'react';
// @ts-expect-error
import ReactQuill, {Blot} from 'react-quill';

import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../icon/svg/loading-icon';
import {Link as LinkShell} from '../link/link';
import {RichTextarea} from '../rich-textarea/rich-textarea';

import {FORMATS, MODULES} from './configuration';
import {EmailComposerToolbar} from './email-composer-toolbar';

import styles from './email-editor.module.css';

import 'react-quill/dist/quill.snow.css';

const EMPTY_LINE = '<div><br></div>';

export const PLACEHOLDER_ACTION_REPLACE = 'REPLACE';
export const PLACEHOLDER_ACTION_INFO = 'INFO';

type PlaceholderAction = 'REPLACE' | 'INFO';

type Props = {
    value: string;
    onChange: (htmlValue: string, textValue: string) => void;
    placeholder?: string;
    fileAttachments?: {
        length: number;
        models: any[];
    };
    errorMessage?: string;
    onClickAttachFile?: () => any;
    onFocus?: () => void;
    onBlur?: () => void;
    onKeyPress?: (event: React.KeyboardEvent) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    onKeyUp?: (event: React.KeyboardEvent) => void;
    fileAttachmentFieldName?: string;
    sendAsReply?: boolean;
    useSignature?: boolean;
    notificationBanner?: React.ComponentType<any>;
    onChangeUseSignature?: () => void;
    allowAddingPlaceholders: boolean;
    /*
    If we show placeholders, should we allow adding custom placeholders. We
    don't allow custom placeholders on email templates that are currently used
    for a stage automation subscription
    */
    allowAddingCustomPlaceholders: boolean;
    customPlaceholderClickAction?: PlaceholderAction;
    signature?: string | null | undefined;
    readOnly?: boolean;
    isLoading?: boolean;
    isBodyFocused?: boolean;
    liveTranscriptionComponent?: React.ReactNode;
    insertLinkComponent?: React.ComponentType<{
        onInsertLink: (link: string) => void;
    }>;
    suggestedReplyToInsert?: string | null | undefined;

    // In some cases (like shared email inbox, we want to make space for a send button to the right of the signature
    // as opposed to it being on a separate line below it)
    hasExtraSpaceForSendButton?: boolean;
};

// This needs to match up with our toolbar's container
// selector, so we'll export it here. See below.
const QUILL_TOOLBAR_CONTAINER_ID = 'toolbar';

let lastId = 0;

function newId(prefix = QUILL_TOOLBAR_CONTAINER_ID) {
    lastId++;

    return `${prefix}-${lastId}`;
}

export type State = {
    customPlaceholderData?: {
        popoverLocation: {
            top: number;
            left: number;
        };
        selectionIndex: number;
        // If the user clicked the blot, we display the onboarding again so we
        // need to know that they clicked it, as apposed to making a new blot
        didClickBlot: boolean;
    };
    placeholderAnchor?: HTMLElement;
};

/**
 * The WYSIWYG editor for email bodies within Nutshell. This allows the
 * user to compose an email, but output HTML to be saved on the server.
 */
export class EmailEditor extends React.PureComponent<Props, State> {
    toolbarId: string;
    quillRef: ReactQuill | null | undefined;
    modules: any;

    static defaultProps = {
        allowAddingPlaceholders: false,
        allowAddingCustomPlaceholders: true,
    };

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (this.quillRef) {
            // If we have the quill editor reference, and either an error message or
            // placeholder, we need to set the quill placeholder. This is because
            // quill doesn't support setting the placeholder dynamicly in the props.
            // See https://github.com/zenoamaro/react-quill/issues/340#issuecomment-376176878
            if (nextProps.errorMessage || nextProps.placeholder) {
                // @ts-expect-error
                this.quillRef.editor.root.dataset.placeholder = nextProps.errorMessage
                    ? nextProps.errorMessage
                    : nextProps.placeholder;
            }
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (
            this.props.suggestedReplyToInsert &&
            prevProps.suggestedReplyToInsert !== this.props.suggestedReplyToInsert
        ) {
            this.handleInsertSuggestedReply(this.props.suggestedReplyToInsert);
        }
    }

    constructor() {
        // @ts-expect-error
        super();

        this.toolbarId = newId();
        // State used for adding placeholders, needs to be top level due to customPlaceholderClickHandler
        this.state = {};
        this.modules = {
            ...MODULES,
            history: {
                // Disable history because custom placeholders totally jack it up
                maxStack: 0,
            },
            placeholder: {
                placeholders: MODULES.placeholder.placeholders,
                // This is kinda hacky, but its the value we use
                // to check if we should replace custom placeholders
                // on click. See placeholder-blot-custom.js
                customPlaceholderClickHandler: this.onCustomPlaceholdersClick,
            },
        };
    }

    render() {
        const hasSignature = this.props.signature && this.props.signature.trim() !== EMPTY_LINE;

        const NotificationBanner = this.props.notificationBanner;

        return (
            <>
                {this.props.isLoading && !this.props.value ? (
                    <div className={styles.loading}>
                        <LoadingIcon fill={colors.orangeLt} />
                    </div>
                ) : undefined}
                <RichTextarea
                    id='email-editor'
                    getQuillRef={(ref) => {
                        // @ts-expect-error
                        this.quillRef = ref;
                    }}
                    placeholderState={this.state}
                    setPlaceholderState={(newState: State) => {
                        this.setState(newState);
                    }}
                    isBodyFocused={this.props.isBodyFocused}
                    forceFocusOnClick={!this.state.customPlaceholderData}
                    readOnly={this.props.readOnly}
                    value={this.props.value}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    onKeyPress={this.props.onKeyPress}
                    onKeyDown={this.props.onKeyDown}
                    onKeyUp={this.props.onKeyUp}
                    placeholder={this.props.placeholder}
                    config={{
                        modules: this.modules,
                        // @ts-expect-error
                        formats: FORMATS,
                        images: true,
                        formatLinks: true,
                        useDivs: true,
                        placeholders: true,
                        toolbarId: this.toolbarId,
                    }}
                    isFullHeight={!this.props.useSignature && !this.props.sendAsReply}
                    onChange={this.props.onChange}
                    notificationBanner={NotificationBanner ? <NotificationBanner /> : undefined}
                    toolbarComponent={
                        <EmailComposerToolbar
                            toolbarId={this.toolbarId}
                            disabled={this.props.readOnly}
                            onToolbarLinkButtonClicked={this.repositionLinkTooltipIfNeeded}
                            fileAttachments={this.props.fileAttachments}
                            fileAttachmentFieldName={this.props.fileAttachmentFieldName}
                            useSignature={this.props.useSignature}
                            onChangeUseSignature={this.props.onChangeUseSignature}
                            onClickAttachFile={this.props.onClickAttachFile}
                            showPlaceholderSelect={this.props.allowAddingPlaceholders}
                            allowAddingCustomPlaceholders={this.props.allowAddingCustomPlaceholders}
                            insertLinkProps={
                                this.props.insertLinkComponent
                                    ? {
                                          component: this.props.insertLinkComponent,
                                          onInsertLink: this.handleInsertLink,
                                      }
                                    : undefined
                            }
                            hasExtraSpaceForSendButton={this.props.hasExtraSpaceForSendButton}
                        />
                    }
                >
                    {this.props.useSignature && !this.props.readOnly ? (
                        <div className={styles['signature-notification']}>
                            <div className={styles['signature-message']}>
                                {hasSignature ? (
                                    <div dangerouslySetInnerHTML={{__html: this.props.signature}} />
                                ) : (
                                    'If you create a Nutshell signature, it will be added here when you send an email.'
                                )}
                            </div>
                            <LinkShell
                                href='/my-account/email-sending'
                                variant='primary'
                                newTab={true}
                                size='small'
                            >
                                {hasSignature ? 'Edit signature' : 'Create signature'}
                            </LinkShell>
                        </div>
                    ) : null}
                    {this.props.sendAsReply ? (
                        <div className={styles['reply-notification']}>
                            <p>This message will be sent as a reply to the previous message.</p>
                        </div>
                    ) : null}
                </RichTextarea>
                {this.props.liveTranscriptionComponent && (
                    <div className={styles.transcribe}>{this.props.liveTranscriptionComponent}</div>
                )}
            </>
        );
    }

    // Helper to reposition the quill link tooltip if it would by default
    // be outside of the bounding editor. Normally this wouldn't matter, but
    // we have an overflow: hidden on the container, so it does not display.
    repositionLinkTooltipIfNeeded = () => {
        setTimeout(() => {
            const toolbar = document.querySelector('.ql-tooltip.ql-editing');
            if (toolbar) {
                const toolbarRect = toolbar.getBoundingClientRect();
                if (this.quillRef) {
                    // @ts-expect-error
                    const editorRect = this.quillRef.editor.root.getBoundingClientRect();
                    if (toolbarRect.x < editorRect.x) {
                        // @ts-expect-error
                        toolbar.style.left = '8px';
                    }

                    if (toolbarRect.y < editorRect.y) {
                        // @ts-expect-error
                        toolbar.style.top = '25px';
                    }
                }
            }
        }, 0);
    };

    handleFocus = () => {
        if (this.props.onFocus) {
            this.props.onFocus();
        }
    };

    handleBlur = () => {
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    onCustomPlaceholdersClick = (quill: ReactQuill, blot: Blot) => {
        if (this.props.customPlaceholderClickAction === PLACEHOLDER_ACTION_REPLACE) {
            // Replace the selected placeholder, inserting the text into the quill
            // editor and highlighting it.
            // Get blot selection
            // @ts-expect-error
            const blotIndex = quill.getIndex(blot);

            // Delete the blot, and set the selection to its old location
            // @ts-expect-error
            quill.deleteText(blotIndex, 1);
            // @ts-expect-error
            quill.insertText(blotIndex, blot.placeholderValue.label);
            // @ts-expect-error
            quill.setSelection(blotIndex, blot.placeholderValue.label.length);
        }

        if (this.props.customPlaceholderClickAction === PLACEHOLDER_ACTION_INFO) {
            // Do something to display the onboarding again
            // Get blot selection
            // @ts-expect-error
            const blotIndex = quill.getIndex(blot);
            // @ts-expect-error
            const bounds = quill.getBounds(blotIndex);
            this.setState({
                customPlaceholderData: {
                    popoverLocation: {
                        top: bounds.top + bounds.height,
                        left: bounds.left,
                    },
                    selectionIndex: blotIndex,
                    didClickBlot: true,
                },
            });
        }
    };

    handleInsertLink = (link: string) => {
        // @ts-expect-error
        if (this.quillRef && this.quillRef.editor) {
            // @ts-expect-error
            const quill = this.quillRef.editor;
            const selection = quill.getSelection();
            const position = selection ? selection.index : quill.getLength();

            quill.insertText(position, link, 'link', link);

            quill.setSelection(position + link.length, 0);
        }
    };

    handleInsertSuggestedReply = (reply: string) => {
        // @ts-expect-error
        if (this.quillRef && this.quillRef.editor) {
            // @ts-expect-error
            const quill = this.quillRef.editor;
            quill.insertText(0, reply);
            quill.setSelection(0);
        }
    };
}
